import React, { useEffect, useMemo, useState, lazy, Suspense } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  ShoppingCart,
  HourglassEmpty,
  Cancel,
  CheckCircle,
  Assessment,
  Group,
  Inventory,
  CurrencyRupee,
} from "@mui/icons-material";
import { baseurl } from "./config/url";

// ✅ Lazy load Graph (charts libs are heavy)
const Graph = lazy(() => import("../components/admin/graph"));

const api = axios.create({
  baseURL: baseurl,
  timeout: 20000,
});

const safeJson = (v, fallback = {}) => {
  try {
    return JSON.parse(v || "");
  } catch {
    return fallback;
  }
};

const Dashboard = () => {
  const navigate = useNavigate();

  const [dashboardData, setDashboardData] = useState({
    totalOrders: 0,
    todaysOrders: 0,
    totalAmount: 0,
    todayTotalAmount: 0,
    totalPendingOrder: 0,
    totalConfirmedOrder: 0,
    totalCanceledOrder: 0,
    totalDeliveredOrder: 0,
    interestedUsers: 0,
    totalUsers: 0,
    totalProduct: 0,
  });

  const [sellerMetrics, setSellerMetrics] = useState({
    totalSellers: 0,
    totalProductsBySellers: 0,
    totalProductsByOGCS: 0,
  });

  const [sellerEarnings, setSellerEarnings] = useState(null);

  // ✅ Separate loading states: cards fast, graphs later
  const [loadingCards, setLoadingCards] = useState(true);
  const [loadingGraphs, setLoadingGraphs] = useState(true);

  const formatINR = (amount) => {
    const num = Number(amount || 0);
    return (
      "₹" +
      num.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  };

  const handleCardClick = (path) => {
    if (!path) return;
    navigate(path);
  };

  useEffect(() => {
    const auth = safeJson(localStorage.getItem("auth"), {});
    const token = auth?.token || localStorage.getItem("token");

    if (!token) {
      localStorage.removeItem("auth");
      localStorage.removeItem("token");
      window.location.href = "/login";
      return;
    }

    const controller = new AbortController();

    const authConfig = {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    };

    // ✅ 1) Load “cards” first (most important)
    const loadCards = async () => {
      setLoadingCards(true);
      try {
        const [
          ordersResponse,
          orderSummaryResponse,
          usersResponse,
          interestedUsersResponse,
          productResponse,
        ] = await Promise.all([
          api.get(`/api/admin/orders`, authConfig),
          api.get(`/api/admin/order-summary`, authConfig),
          api.get(`/api/admin/user/all-users`, authConfig),
          api.get(`/api/admin/interested-users`, authConfig),
          api.get(`/api/product`, { signal: controller.signal }), // public endpoint
        ]);

        setDashboardData({
          totalOrders: ordersResponse.data?.totalOrders || 0,
          todaysOrders: ordersResponse.data?.todaysOrders || 0,
          totalAmount: ordersResponse.data?.totalAmount || 0,
          todayTotalAmount: ordersResponse.data?.todayTotalAmount || 0,

          totalPendingOrder: orderSummaryResponse.data?.totalPendingOrder || 0,
          totalConfirmedOrder: orderSummaryResponse.data?.totalConfirmedOrder || 0,
          totalCanceledOrder: orderSummaryResponse.data?.totalCanceledOrder || 0,
          totalDeliveredOrder: orderSummaryResponse.data?.totalDeliveredOrder || 0,

          totalUsers: usersResponse.data?.totalUsers || 0,
          interestedUsers: interestedUsersResponse.data?.totalInterestedUsers || 0,
          totalProduct: productResponse.data?.totalProducts || 0,
        });
      } catch (error) {
        if (axios.isCancel?.(error)) return;
        if (error?.name === "CanceledError") return;

        console.error("Dashboard (cards) error:", error);
        if (error.response?.status === 401) {
          localStorage.removeItem("auth");
          localStorage.removeItem("token");
          window.location.href = "/login";
        }
      } finally {
        setLoadingCards(false);
      }
    };

    // ✅ 2) Load “graphs” later (less critical)
    const loadGraphs = async () => {
      setLoadingGraphs(true);
      try {
        const [sellerMetricsResponse, earningsResponse] = await Promise.all([
          api.get(`/api/admin/seller-metrics`, authConfig),
          api.get(`/api/admin/earnings`, authConfig),
        ]);

        setSellerMetrics(sellerMetricsResponse.data || {});
        setSellerEarnings(earningsResponse.data || null);
      } catch (error) {
        if (axios.isCancel?.(error)) return;
        if (error?.name === "CanceledError") return;
        console.error("Dashboard (graphs) error:", error);
      } finally {
        setLoadingGraphs(false);
      }
    };

    loadCards().then(loadGraphs);

    return () => controller.abort();
  }, []);

  // ✅ Memoize graph data (prevents re-render churn in chart libs)
  const graphData = useMemo(
    () => ({
      labels: ["Total Users", "Total Orders", "Interested Users"],
      datasets: [
        {
          label: "Counts",
          data: [
            dashboardData.totalUsers,
            dashboardData.totalOrders,
            dashboardData.interestedUsers,
          ],
          backgroundColor: ["#FF6384", "#36A2EB", "#FFCE56"],
        },
      ],
    }),
    [dashboardData.totalUsers, dashboardData.totalOrders, dashboardData.interestedUsers]
  );

  const sellerGraphData = useMemo(
    () => ({
      labels: ["Total Sellers", "Products by Sellers", "Products by OGCS"],
      datasets: [
        {
          label: "Seller Metrics",
          data: [
            sellerMetrics.totalSellers,
            sellerMetrics.totalProductsBySellers,
            sellerMetrics.totalProductsByOGCS,
          ],
          backgroundColor: ["#4BC0C0", "#FF9F40", "#9966FF"],
        },
      ],
    }),
    [
      sellerMetrics.totalSellers,
      sellerMetrics.totalProductsBySellers,
      sellerMetrics.totalProductsByOGCS,
    ]
  );

  const earningsGraphData = useMemo(() => {
    if (!sellerEarnings) return null;
    return {
      labels: ["Visibility Amount", "Power Pack Amount", "Deposit Amount", "Registration Amount"],
      datasets: [
        {
          label: "Earnings ₹",
          data: [
            sellerEarnings.visibilityAmount || 0,
            sellerEarnings.powerPackAmount || 0,
            sellerEarnings.depositAmount || 0,
            sellerEarnings.registrationAmount || 0,
          ],
          backgroundColor: ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0"],
        },
      ],
    };
  }, [sellerEarnings]);

  const cards = useMemo(
    () => [
      { icon: <ShoppingCart />, value: dashboardData.todaysOrders, label: "Today's Orders", path: "/orders/all" },
      { icon: <ShoppingCart />, value: dashboardData.totalOrders, label: "Total Orders", path: "/orders/all" },
      { icon: <CurrencyRupee />, value: formatINR(dashboardData.todayTotalAmount), label: "Today's Earnings", path: "/orders/all" },
      { icon: <CurrencyRupee />, value: formatINR(dashboardData.totalAmount), label: "Total Earnings", path: "/orders/all" },
      { icon: <HourglassEmpty />, value: dashboardData.totalPendingOrder, label: "Pending Orders", path: "/orders/pending" },
      { icon: <CheckCircle />, value: dashboardData.totalConfirmedOrder, label: "Confirmed Orders", path: "/orders/confirmed" },
      { icon: <Cancel />, value: dashboardData.totalCanceledOrder, label: "Canceled Orders", path: "/orders/canceled" },
      { icon: <Assessment />, value: dashboardData.totalDeliveredOrder, label: "Delivered Orders", path: "/orders/delivered" },
      { icon: <Group />, value: dashboardData.totalUsers, label: "Total Users", path: "/users/all" },
      { icon: <Inventory />, value: dashboardData.totalProduct, label: "Total Products", path: "/products" },
    ],
    [dashboardData] // ok because values are primitive; for more optimization use explicit deps
  );

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <h1 className="text-3xl font-bold mb-6">Dashboard</h1>

      {/* Cards */}
      {loadingCards ? (
        <div className="text-center p-6 text-xl">Loading Dashboard...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
          {cards.map((item, index) => (
            <div
              key={index}
              onClick={() => handleCardClick(item.path)}
              className="p-4 bg-white shadow rounded-lg text-center border border-gray-200
              hover:shadow-lg hover:-translate-y-1 cursor-pointer transition"
            >
              <div className="flex justify-center text-blue-600 mb-3">{item.icon}</div>
              <h2 className="text-2xl font-bold text-blue-600">{item.value}</h2>
              <p className="text-gray-500 mt-2">{item.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Graphs (lazy + suspense) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white shadow-lg rounded-lg p-6">
          <h2 className="text-xl font-bold mb-4 text-center">User Metrics</h2>

          {loadingGraphs ? (
            <div className="text-center text-gray-500">Loading charts...</div>
          ) : (
            <Suspense fallback={<div className="text-center text-gray-500">Loading chart module...</div>}>
              <Graph data={graphData} type="Doughnut" title="User Metrics" />
            </Suspense>
          )}
        </div>

        <div className="bg-white shadow-lg rounded-lg p-6">
          <h2 className="text-xl font-bold mb-4 text-center">Seller Metrics</h2>

          {loadingGraphs ? (
            <div className="text-center text-gray-500">Loading charts...</div>
          ) : (
            <Suspense fallback={<div className="text-center text-gray-500">Loading chart module...</div>}>
              <Graph data={sellerGraphData} type="Doughnut" title="Seller Metrics" />
            </Suspense>
          )}
        </div>
      </div>

      {!loadingGraphs && earningsGraphData && (
        <div className="bg-white shadow-lg rounded-lg p-6 mt-6">
          <h2 className="text-xl font-bold mb-4 text-center">Earnings Overview</h2>
          <Suspense fallback={<div className="text-center text-gray-500">Loading chart module...</div>}>
            <Graph data={earningsGraphData} type="Line" title="Earnings Breakdown" />
          </Suspense>
        </div>
      )}
    </div>
  );
};

export default Dashboard;

















































// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import { useNavigate } from "react-router-dom";
// import {
//   ShoppingCart,
//   HourglassEmpty,
//   Cancel,
//   CheckCircle,
//   Assessment,
//   Group,
//   Inventory,
//   CurrencyRupee,
// } from "@mui/icons-material";
// // import { CurrencyRupee } from "@mui/icons-material";
// import { baseurl } from "./config/url";
// import Graph from "../components/admin/graph";

// const Dashboard = () => {
//   const navigate = useNavigate();

//   const [dashboardData, setDashboardData] = useState({
//     totalOrders: 0,
//     todaysOrders: 0,
//     totalAmount: 0,
//     todayTotalAmount: 0,
//     totalPendingOrder: 0,
//     totalConfirmedOrder: 0,
//     totalCanceledOrder: 0,
//     totalDeliveredOrder: 0,
//     interestedUsers: 0,
//     totalUsers: 0,
//     totalProduct: 0,
//   });

//   const [sellerMetrics, setSellerMetrics] = useState({
//     totalSellers: 0,
//     totalProductsBySellers: 0,
//     totalProductsByOGCS: 0,
//   });

//   const [sellerEarnings, setSellerEarnings] = useState(null);
//   const [isLoading, setIsLoading] = useState(true);

//   // ✅ INR Currency formatter
//   const formatINR = (amount) => {
//     const num = Number(amount || 0);
//     return "₹" + num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
//   };

//   const handleCardClick = (path) => {
//     if (!path) return;
//     navigate(path);
//   };

//   useEffect(() => {
//     const fetchDashboardData = async () => {
//       setIsLoading(true);

//       const auth = (() => {
//         try {
//           return JSON.parse(localStorage.getItem("auth") || "{}");
//         } catch {
//           return {};
//         }
//       })();

//       const token = auth?.token || localStorage.getItem("token");

//       if (!token) {
//         alert("Session expired. Redirecting to login.");
//         localStorage.removeItem("auth");
//         localStorage.removeItem("token");
//         window.location.href = "/login";
//         return;
//       }

//       const config = {
//         headers: {
//           Authorization: `Bearer ${token}`,
//         },
//       }; 

//       try {
//         const [
//           ordersResponse,
//           orderSummaryResponse,
//           usersResponse,
//           interestedUsersResponse,
//           productResponse,
//           sellerMetricsResponse,
//           earningsResponse,
//         ] = await Promise.all([
//           axios.get(`${baseurl}/api/admin/orders`, config),
//           axios.get(`${baseurl}/api/admin/order-summary`, config),
//           axios.get(`${baseurl}/api/admin/user/all-users`, config),
//           axios.get(`${baseurl}/api/admin/interested-users`, config),
//           axios.get(`${baseurl}/api/product`),
//           axios.get(`${baseurl}/api/admin/seller-metrics`, config),
//           axios.get(`${baseurl}/api/admin/earnings`, config),
//         ]);

//         setDashboardData({
//           totalOrders: ordersResponse.data?.totalOrders || 0,
//           todaysOrders: ordersResponse.data?.todaysOrders || 0,
//           totalAmount: ordersResponse.data?.totalAmount || 0,
//           todayTotalAmount: ordersResponse.data?.todayTotalAmount || 0,
//           totalPendingOrder: orderSummaryResponse.data?.totalPendingOrder || 0,
//           totalConfirmedOrder:
//             orderSummaryResponse.data?.totalConfirmedOrder || 0,
//           totalCanceledOrder:
//             orderSummaryResponse.data?.totalCanceledOrder || 0,
//           totalDeliveredOrder:
//             orderSummaryResponse.data?.totalDeliveredOrder || 0,
//           totalUsers: usersResponse.data?.totalUsers || 0,
//           interestedUsers:
//             interestedUsersResponse.data?.totalInterestedUsers || 0,
//           totalProduct: productResponse.data?.totalProducts || 0,
//         });

//         setSellerMetrics(sellerMetricsResponse.data || {});
//         setSellerEarnings(earningsResponse.data || null);
//       } catch (error) {
//         console.error("Dashboard error:", error);

//         if (error.response?.status === 401) {
//           alert("Session expired. Redirecting to login.");
//           localStorage.removeItem("auth");
//           localStorage.removeItem("token");
//         }
//       } finally {
//         setIsLoading(false);
//       }
//     };

//     fetchDashboardData();
//   }, []);

//   if (isLoading) {
//     return <div className="text-center p-6 text-xl">Loading Dashboard...</div>;
//   }

//   // 📊 Graph Data
//   const graphData = {
//     labels: ["Total Users", "Total Orders", "Interested Users"],
//     datasets: [
//       {
//         label: "Counts",
//         data: [
//           dashboardData.totalUsers,
//           dashboardData.totalOrders,
//           dashboardData.interestedUsers,
//         ],
//         backgroundColor: ["#FF6384", "#36A2EB", "#FFCE56"],
//       },
//     ],
//   };

//   const sellerGraphData = {
//     labels: ["Total Sellers", "Products by Sellers", "Products by OGCS"],
//     datasets: [
//       {
//         label: "Seller Metrics",
//         data: [
//           sellerMetrics.totalSellers,
//           sellerMetrics.totalProductsBySellers,
//           sellerMetrics.totalProductsByOGCS,
//         ],
//         backgroundColor: ["#4BC0C0", "#FF9F40", "#9966FF"],
//       },
//     ],
//   };

//   const earningsGraphData = sellerEarnings
//     ? {
//       labels: [
//         "Visibility Amount",
//         "Power Pack Amount",
//         "Deposit Amount",
//         "Registration Amount",
//       ],
//       datasets: [
//         {
//           label: "Earnings ₹",
//           data: [
//             sellerEarnings.visibilityAmount || 0,
//             sellerEarnings.powerPackAmount || 0,
//             sellerEarnings.depositAmount || 0,
//             sellerEarnings.registrationAmount || 0,
//           ],
//           backgroundColor: ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0"],
//         },
//       ],
//     }
//     : null;

//   const cards = [
//     {
//       icon: <ShoppingCart />,
//       value: dashboardData.todaysOrders,
//       label: "Today's Orders",
//       path: "/orders/all",
//     },
//     {
//       icon: <ShoppingCart />,
//       value: dashboardData.totalOrders,
//       label: "Total Orders",
//       path: "/orders/all",
//     },
//     {
//       icon: <CurrencyRupee />,
//       value: formatINR(dashboardData.todayTotalAmount),
//       label: "Today's Earnings",
//       path: "/orders/all",
//     },
//     {
//       icon: <CurrencyRupee />,
//       value: formatINR(dashboardData.totalAmount),
//       label: "Total Earnings",
//       path: "/orders/all",
//     },
//     {
//       icon: <HourglassEmpty />,
//       value: dashboardData.totalPendingOrder,
//       label: "Pending Orders",
//       path: "/orders/pending",
//     },
//     {
//       icon: <CheckCircle />,
//       value: dashboardData.totalConfirmedOrder,
//       label: "Confirmed Orders",
//       path: "/orders/confirmed",
//     },
//     {
//       icon: <Cancel />,
//       value: dashboardData.totalCanceledOrder,
//       label: "Canceled Orders",
//       path: "/orders/canceled",
//     },
//     {
//       icon: <Assessment />,
//       value: dashboardData.totalDeliveredOrder,
//       label: "Delivered Orders",
//       path: "/orders/delivered",
//     },
//     {
//       icon: <Group />,
//       value: dashboardData.totalUsers,
//       label: "Total Users",
//       path: "/users/all",
//     },
//     {
//       icon: <Inventory />,
//       value: dashboardData.totalProduct,
//       label: "Total Products",
//       path: "/products",
//     },
//   ];

//   return (
//     <div className="p-6 bg-gray-100 min-h-screen">
//       <h1 className="text-3xl font-bold mb-6">Dashboard</h1>

//       {/* Dashboard Cards */}
//       <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
//         {cards.map((item, index) => (
//           <div
//             key={index}
//             onClick={() => handleCardClick(item.path)}
//             className="p-4 bg-white shadow rounded-lg text-center border border-gray-200
//             hover:shadow-lg hover:-translate-y-1 cursor-pointer transition"
//           >
//             <div className="flex justify-center text-blue-600 mb-3">
//               {item.icon}
//             </div>

//             <h2 className="text-2xl font-bold text-blue-600">{item.value}</h2>

//             <p className="text-gray-500 mt-2">{item.label}</p>
//           </div>
//         ))}
//       </div>

//       {/* Graph Section */}
//       <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//         <div className="bg-white shadow-lg rounded-lg p-6">
//           <h2 className="text-xl font-bold mb-4 text-center">
//             User Metrics
//           </h2>
//           <Graph data={graphData} type="Doughnut" title="User Metrics" />
//         </div>

//         <div className="bg-white shadow-lg rounded-lg p-6">
//           <h2 className="text-xl font-bold mb-4 text-center">
//             Seller Metrics
//           </h2>
//           <Graph data={sellerGraphData} type="Doughnut" title="Seller Metrics" />
//         </div>
//       </div>

//       {earningsGraphData && (
//         <div className="bg-white shadow-lg rounded-lg p-6 mt-6">
//           <h2 className="text-xl font-bold mb-4 text-center">
//             Earnings Overview
//           </h2>
//           <Graph
//             data={earningsGraphData}
//             type="Line"
//             title="Earnings Breakdown"
//           />
//         </div>
//       )}
//     </div>
//   );
// };

// export default Dashboard;